import type { ComposeAttachment, ComposeDraft } from "@/lib/types/compose";
import { linkifyPlainUrlsInRoot } from "@/lib/compose/composeAutolink";
import { normalizeRichHtml } from "@/lib/compose/richTextFormat";

const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;

export function looksLikeHtml(value: string): boolean {
  return HTML_TAG_RE.test(value);
}

/** Échappe le texte brut pour insertion HTML sûre. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function plainTextToHtml(text: string): string {
  if (!text.trim()) return "";
  return escapeHtml(text).replace(/\n/g, "<br>");
}

export function htmlToPlainText(html: string): string {
  if (!html.trim()) return "";
  if (typeof document === "undefined") {
    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  const root = document.createElement("div");
  root.innerHTML = html;
  return (root.innerText || root.textContent || "").replace(/\u00a0/g, " ").trim();
}

export function isRichSignatureHtml(html: string): boolean {
  return /<(table|tbody|tr|td|th|img)\b/i.test(html);
}

export function signatureToHtml(signature: string): string {
  const trimmed = signature.trim();
  if (!trimmed) return "";
  if (looksLikeHtml(trimmed)) return trimmed;
  return `<div>${plainTextToHtml(trimmed)}</div>`;
}

export function wrapSignatureHtml(signatureHtml: string): string {
  if (!signatureHtml.trim()) return "";
  const rich = isRichSignatureHtml(signatureHtml);
  const style = rich
    ? "margin-top:16px;padding-top:12px;border-top:1px solid #e5e7eb;"
    : "margin-top:16px;padding-top:12px;border-top:1px solid #e5e7eb;color:#6b7280;font-size:14px;line-height:1.5;font-family:system-ui,-apple-system,sans-serif;";
  const richClass = rich ? " mail-compose-signature--rich" : "";
  return `<div data-mail-signature="1" class="mail-compose-signature${richClass}" style="${style}">${signatureHtml}</div>`;
}

/** Styles inline pour clients mail (Gmail, Outlook…) — les classes CSS ne suivent pas. */
const SIGNATURE_OUTBOUND_STYLE =
  "margin-top:16px;padding-top:12px;border-top:1px solid #e5e7eb;color:#6b7280;font-size:14px;line-height:1.5;font-family:system-ui,-apple-system,sans-serif;";
const QUOTE_OUTBOUND_STYLE =
  "margin-top:12px;padding-top:12px;border-top:1px solid #e5e7eb;color:#6b7280;font-size:14px;line-height:1.5;font-family:system-ui,-apple-system,sans-serif;";
const LINK_OUTBOUND_STYLE = "color:#6366f1;text-decoration:underline;";

function mergeInlineStyle(existing: string | null, required: string): string {
  const base = (existing ?? "").trim().replace(/;+\s*$/, "");
  if (!base) return required;
  if (base.includes("margin-top:") || base.includes("border-top:")) return base;
  return `${base};${required}`;
}

/** Applique styles inline sur signature / citation avant envoi SMTP. */
export function prepareComposeHtmlForSend(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return trimmed;

  if (typeof DOMParser === "undefined") {
    return trimmed;
  }

  const doc = new DOMParser().parseFromString(`<div id="mail-compose-root">${trimmed}</div>`, "text/html");
  const root = doc.getElementById("mail-compose-root");
  if (!root) return trimmed;

  for (const node of root.querySelectorAll('[data-mail-signature="1"]')) {
    const el = node as HTMLElement;
    el.setAttribute("style", mergeInlineStyle(el.getAttribute("style"), SIGNATURE_OUTBOUND_STYLE));
  }

  for (const node of root.querySelectorAll('[data-mail-quote="1"]')) {
    const el = node as HTMLElement;
    el.setAttribute("style", mergeInlineStyle(el.getAttribute("style"), QUOTE_OUTBOUND_STYLE));
  }

  linkifyPlainUrlsInRoot(root);

  for (const link of root.querySelectorAll("a")) {
    if (!link.getAttribute("style")?.trim()) {
      link.setAttribute("style", LINK_OUTBOUND_STYLE);
    }
    if (!link.getAttribute("rel")) {
      link.setAttribute("rel", "noopener noreferrer");
    }
    if (!link.getAttribute("target")) {
      link.setAttribute("target", "_blank");
    }
  }

  return root.innerHTML.trim();
}

/** Nettoie une signature enregistrée (HTML brut ou éditeur riche). */
export function sanitizeSignatureHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return "";

  if (typeof DOMParser === "undefined") {
    return trimmed;
  }

  const doc = new DOMParser().parseFromString(trimmed, "text/html");
  for (const node of doc.querySelectorAll("script, iframe, object, embed")) {
    node.remove();
  }
  return doc.body.innerHTML.trim();
}

/**
 * Duplique les règles `@media (prefers-color-scheme: dark)` pour l’aperçu in-app
 * (`[data-theme="dark"]`) — le toggle thème Aurélia n’utilise pas prefers-color-scheme.
 * N’altère pas le HTML envoyé aux clients mail.
 */
export function adaptSignatureHtmlForAppPreview(html: string): string {
  const trimmed = html.trim();
  if (!trimmed || !/<style[\s>]/i.test(trimmed)) return trimmed;

  const styleRe = /<style([^>]*)>([\s\S]*?)<\/style>/gi;
  let changed = false;

  const next = trimmed.replace(styleRe, (_full, attrs: string, css: string) => {
    const mirrored = mirrorPrefersDarkCssForAppPreview(css);
    if (!mirrored) return _full;
    changed = true;
    const attrStr = typeof attrs === "string" ? attrs : "";
    return `<style${attrStr}>${css}${mirrored}</style>`;
  });

  return changed ? next : trimmed;
}

function mirrorPrefersDarkCssForAppPreview(css: string): string {
  const mediaRe = /@media\s*\(\s*prefers-color-scheme\s*:\s*dark\s*\)\s*\{([\s\S]*?)\}/gi;
  const chunks: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = mediaRe.exec(css)) !== null) {
    const inner = match[1]?.trim() ?? "";
    if (!inner) continue;

    for (const rule of inner.split("}")) {
      const chunk = rule.trim();
      if (!chunk) continue;
      const brace = chunk.indexOf("{");
      if (brace < 0) continue;
      const selectors = chunk.slice(0, brace).trim();
      const decls = chunk.slice(brace + 1).trim();
      if (!selectors || !decls) continue;

      const scoped = selectors
        .split(",")
        .map((sel) => `[data-theme="dark"] .mail-signature-preview-scope ${sel.trim()}`)
        .join(", ");
      chunks.push(`${scoped} { ${decls} }`);
    }
  }

  return chunks.length > 0 ? `\n${chunks.join("\n")}` : "";
}

export function normalizeStoredSignatureHtml(html: string): string {
  const sanitized = sanitizeSignatureHtml(html);
  if (!sanitized) return "";
  if (htmlToPlainText(sanitized).length === 0) return "";
  return sanitized;
}

export function wrapQuoteHtml(quoteHtml: string): string {
  if (!quoteHtml.trim()) return "";
  const style =
    "margin-top:12px;padding-top:12px;border-top:1px solid #e5e7eb;color:#6b7280;font-size:14px;line-height:1.5;font-family:system-ui,-apple-system,sans-serif;";
  return `<div data-mail-quote="1" class="mail-compose-quote" style="${style}">${quoteHtml}</div>`;
}

export function normalizeComposeHtml(html: string, fallbackText = ""): string {
  const trimmed = html.trim();
  if (trimmed) return trimmed;
  return plainTextToHtml(fallbackText);
}

export function buildNewComposeBody(_signature?: string): { bodyHtml: string; bodyText: string } {
  return { bodyHtml: "<p><br></p>", bodyText: "" };
}

export function buildReplyComposeBody(
  _signature: string,
  quoteText: string,
  quoteHtml: string
): { bodyHtml: string; bodyText: string } {
  const quoteBlock = wrapQuoteHtml(quoteHtml || plainTextToHtml(quoteText));
  return {
    bodyHtml: `<p><br></p><p><br></p>${quoteBlock}`,
    bodyText: `\n\n${quoteText}`,
  };
}

/** Retire la signature embarquée ; la citation reste dans l’éditeur (modifiable). */
export function splitComposeBodyHtml(html: string): { messageHtml: string } {
  const trimmed = html.trim();
  if (!trimmed) {
    return { messageHtml: "<p><br></p>" };
  }

  if (typeof DOMParser === "undefined") {
    return { messageHtml: trimmed };
  }

  const doc = new DOMParser().parseFromString(`<div id="mail-compose-split">${trimmed}</div>`, "text/html");
  const root = doc.getElementById("mail-compose-split");
  if (!root) {
    return { messageHtml: trimmed };
  }

  for (const node of root.querySelectorAll('[data-mail-signature="1"]')) {
    node.remove();
  }

  const messageHtml = root.innerHTML.trim() || "<p><br></p>";
  return { messageHtml };
}

/** Sessions mail55 : citation séparée → réinjectée dans l’éditeur. */
export function mergeLegacyComposeQuoteHtml(messageHtml: string, composeQuoteHtml?: string): string {
  const quote = composeQuoteHtml?.trim() ?? "";
  if (!quote) return messageHtml;
  if (messageHtml.includes('data-mail-quote="1"')) return messageHtml;
  return `${messageHtml}${quote}`;
}

export function assembleComposeBodyHtml(messageHtml: string, signature: string): string {
  const message = messageHtml.trim() || "<p><br></p>";
  const sigHtml = wrapSignatureHtml(signatureToHtml(signature));
  if (!sigHtml) return message;

  if (typeof DOMParser === "undefined") {
    return `${message}<p><br></p>${sigHtml}`;
  }

  const doc = new DOMParser().parseFromString(`<div id="mail-compose-assemble">${message}</div>`, "text/html");
  const root = doc.getElementById("mail-compose-assemble");
  if (!root) {
    return `${message}<p><br></p>${sigHtml}`;
  }

  for (const node of root.querySelectorAll('[data-mail-signature="1"]')) {
    node.remove();
  }

  const quoteNode = root.querySelector('[data-mail-quote="1"]');
  if (quoteNode) {
    quoteNode.insertAdjacentHTML("beforebegin", `<p><br></p>${sigHtml}`);
    return root.innerHTML.trim();
  }

  const cleaned = root.innerHTML.trim() || "<p><br></p>";
  return `${cleaned}<p><br></p>${sigHtml}`;
}

export function assembleComposeDraft(
  draft: Pick<ComposeDraft, "bodyHtml" | "bodyText">,
  signature: string
): { bodyHtml: string; bodyText: string } {
  const bodyHtml = assembleComposeBodyHtml(draft.bodyHtml, signature);
  return {
    bodyHtml,
    bodyText: draft.bodyText?.trim() || htmlToPlainText(bodyHtml),
  };
}

/** Réponse inline : fusionne citation + signature avant envoi / brouillon. */
export function assembleInlineReplyOutbound(
  draft: Pick<ComposeDraft, "bodyHtml" | "bodyText" | "composeQuoteHtml">,
  signature: string
): { bodyHtml: string; bodyText: string } {
  const editorHtml = draft.bodyHtml.trim() || "<p><br></p>";
  const withQuote = mergeLegacyComposeQuoteHtml(editorHtml, draft.composeQuoteHtml);
  const assembled = assembleComposeDraft({ bodyHtml: withQuote, bodyText: draft.bodyText }, signature);
  const quotePlain = draft.composeQuoteHtml?.trim() ? htmlToPlainText(draft.composeQuoteHtml) : "";
  const editorPlain = htmlToPlainText(editorHtml).trim();
  const bodyText = [editorPlain, quotePlain].filter(Boolean).join("\n\n") || assembled.bodyText;
  return { bodyHtml: assembled.bodyHtml, bodyText };
}

/** Assure bodyHtml sur les brouillons issus d’anciennes versions. */
export function migrateComposeDraftBody(draft: {
  bodyText: string;
  bodyHtml?: string;
}): { bodyText: string; bodyHtml: string } {
  const bodyText = draft.bodyText ?? "";
  const bodyHtml = draft.bodyHtml?.trim()
    ? draft.bodyHtml
    : normalizeComposeHtml("", bodyText);
  return { bodyText, bodyHtml };
}

/** Corps vide (éditeur par défaut `<p><br></p>`, signature seule sans texte utile, etc.). */
export function isComposeBodyEmpty(bodyHtml: string, bodyText?: string): boolean {
  if (bodyText?.trim()) return false;
  return htmlToPlainText(bodyHtml).length === 0;
}

/** Au moins un objet, un corps ou une pièce jointe prête. */
export function hasComposeSendableContent(options: {
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  attachments?: ComposeAttachment[];
}): boolean {
  if (options.subject?.trim()) return true;
  if (!isComposeBodyEmpty(options.bodyHtml ?? "", options.bodyText)) return true;
  return (options.attachments?.some((file) => !file.loading) ?? false);
}

/** CSS injecté à la lecture IMAP (mail_imap_wrap_html_document) — hors envoi SMTP. */
const MAIL_DISPLAY_BODY_CSS_RE =
  /body\s*\{\s*margin\s*:\s*0\s*;\s*padding\s*:\s*16px\s*;\s*font-family\s*:\s*system-ui\s*,\s*sans-serif/i;

function looksLikeMailDisplayWrapper(html: string, doc: Document): boolean {
  if (doc.querySelector('style[data-mail-theme]')) return true;
  for (const style of doc.querySelectorAll("head style")) {
    if (MAIL_DISPLAY_BODY_CSS_RE.test(style.textContent ?? "")) return true;
  }
  return MAIL_DISPLAY_BODY_CSS_RE.test(html);
}

function stripMailDisplayDocumentWrapperFallback(html: string): string {
  if (!MAIL_DISPLAY_BODY_CSS_RE.test(html)) return html;
  const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return match?.[1]?.trim() ?? html;
}

/** Retire le document HTML de lecture (styles Aurélia) avant édition ou envoi. */
export function stripMailDisplayDocumentWrapper(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return trimmed;

  const hasDocument = /^\s*<!DOCTYPE/i.test(trimmed) || /^\s*<html[\s>]/i.test(trimmed);
  if (!hasDocument) return trimmed;

  if (typeof DOMParser === "undefined") {
    return stripMailDisplayDocumentWrapperFallback(trimmed);
  }

  const doc = new DOMParser().parseFromString(trimmed, "text/html");
  if (!looksLikeMailDisplayWrapper(trimmed, doc)) return trimmed;

  const bodyHtml = doc.body?.innerHTML?.trim() ?? "";
  return bodyHtml || trimmed;
}

export function normalizeComposeOutboundBody(
  bodyHtml: string,
  bodyText?: string
): { bodyHtml?: string; bodyText: string } {
  const cleanedHtml = prepareComposeHtmlForSend(
    stripMailDisplayDocumentWrapper(normalizeRichHtml(bodyHtml))
  );
  if (!isComposeBodyEmpty(cleanedHtml, bodyText)) {
    const plain = bodyText?.trim() || htmlToPlainText(cleanedHtml);
    return {
      bodyHtml: cleanedHtml.trim() || undefined,
      bodyText: plain,
    };
  }
  return { bodyHtml: undefined, bodyText: "" };
}
